import { clamp } from '../config/music';
import { t } from '../i18n';

/**
 * Two-thumb range slider.
 *
 * Replaces a pair of stacked native <input type="range"> elements made
 * click-through with `pointer-events: none` except on their thumbs. That trick
 * left the control feeling stuck: whichever input came last in the DOM won
 * every overlap, so the lower thumb became ungrabbable once the two met, and
 * the handlers had to write values back mid-drag, fighting the thumb being
 * dragged. Here the nearest thumb always wins, values are clamped against each
 * other, and each thumb is a real ARIA slider with keyboard support.
 */

export interface RangeSliderOptions {
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  /** Minimum distance kept between the two thumbs. */
  readonly minGap?: number;
  readonly labels?: readonly [string, string];
  /** Human-readable value for screen readers, e.g. 48 → "C4". */
  readonly valueText?: (value: number) => string;
  readonly onInput: (low: number, high: number) => void;
}

export interface RangeSlider {
  /** Write values in without emitting onInput. Ignored while dragging. */
  setValues(low: number, high: number): void;
  readonly low: number;
  readonly high: number;
}

type ThumbIndex = 0 | 1;

/**
 * Either one endpoint is moving, or the whole selection is being slid along
 * while keeping its width — which is how you change the starting note without
 * having to re-set the span afterwards.
 */
type Drag =
  | { readonly kind: 'thumb'; readonly index: ThumbIndex }
  | { readonly kind: 'range'; readonly grabbedAt: number; readonly fromLow: number; readonly span: number }
  | null;

export function createRangeSlider(host: HTMLElement, opts: RangeSliderOptions): RangeSlider {
  const step = opts.step ?? 1;
  const minGap = opts.minGap ?? step;
  const labels = opts.labels ?? ['Minim', 'Maxim'];

  let low = opts.min;
  let high = opts.max;
  let drag: Drag = null;

  host.classList.add('rs');
  host.replaceChildren();

  const track = document.createElement('div');
  track.className = 'rs-track';
  const fill = document.createElement('div');
  fill.className = 'rs-fill';
  fill.setAttribute('role', 'button');
  fill.setAttribute('tabindex', '0');
  fill.setAttribute('aria-label', t('range.drag.aria'));
  host.append(track, fill);

  const thumbs: HTMLElement[] = [0, 1].map((i) => {
    const t = document.createElement('div');
    t.className = 'rs-thumb';
    t.setAttribute('role', 'slider');
    t.setAttribute('tabindex', '0');
    t.setAttribute('aria-label', labels[i] ?? '');
    t.setAttribute('aria-valuemin', String(opts.min));
    t.setAttribute('aria-valuemax', String(opts.max));
    host.appendChild(t);
    return t;
  });

  const quantize = (v: number): number =>
    clamp(Math.round((v - opts.min) / step) * step + opts.min, opts.min, opts.max);

  const ratio = (v: number): number => (v - opts.min) / (opts.max - opts.min);

  function paint(): void {
    const lo = ratio(low) * 100;
    const hi = ratio(high) * 100;
    fill.style.left = `${lo}%`;
    fill.style.width = `${hi - lo}%`;
    if (opts.valueText) {
      fill.setAttribute('aria-valuetext', `${opts.valueText(low)} – ${opts.valueText(high)}`);
    }
    const values: readonly [number, number] = [low, high];
    thumbs.forEach((t, i) => {
      const v = values[i] as number;
      t.style.left = `${ratio(v) * 100}%`;
      t.setAttribute('aria-valuenow', String(v));
      if (opts.valueText) t.setAttribute('aria-valuetext', opts.valueText(v));
    });
  }

  /** Apply a new value to one thumb, keeping the pair ordered and separated. */
  function commit(index: ThumbIndex, raw: number, emit: boolean): void {
    const v = quantize(raw);
    if (index === 0) {
      const next = Math.min(v, high - minGap);
      if (next === low) return;
      low = clamp(next, opts.min, opts.max);
    } else {
      const next = Math.max(v, low + minGap);
      if (next === high) return;
      high = clamp(next, opts.min, opts.max);
    }
    paint();
    if (emit) opts.onInput(low, high);
  }

  const valueAt = (clientX: number): number => {
    const rect = host.getBoundingClientRect();
    if (rect.width === 0) return opts.min;
    const r = clamp((clientX - rect.left) / rect.width, 0, 1);
    return opts.min + r * (opts.max - opts.min);
  };

  /**
   * Move the whole selection so its low end lands `delta` away, preserving the
   * span exactly. Clamping stops the selection at the ends instead of letting
   * it compress against them.
   */
  function shiftRangeTo(fromLow: number, span: number, delta: number, emit: boolean): void {
    const quantizedDelta = Math.round(delta / step) * step;
    const nextLow = clamp(fromLow + quantizedDelta, opts.min, opts.max - span);
    if (nextLow === low && nextLow + span === high) return;
    low = nextLow;
    high = nextLow + span;
    paint();
    if (emit) opts.onInput(low, high);
  }

  const shiftBy = (delta: number): void => shiftRangeTo(low, high - low, delta, true);

  /** The nearest thumb wins; ties go to the one that can actually move there. */
  function nearestThumb(value: number): ThumbIndex {
    const dLow = Math.abs(value - low);
    const dHigh = Math.abs(value - high);
    if (dLow < dHigh) return 0;
    if (dHigh < dLow) return 1;
    return value < low ? 0 : 1;
  }

  host.addEventListener('pointerdown', (e) => {
    const target = e.target;
    const value = valueAt(e.clientX);

    // Synthetic pointer events (tests, some assistive tools) have no live
    // pointer to capture; dragging still works through the host listeners.
    const capture = (): void => {
      try {
        host.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    if (target === thumbs[0] || target === thumbs[1]) {
      const index: ThumbIndex = target === thumbs[0] ? 0 : 1;
      drag = { kind: 'thumb', index };
      thumbs[index]?.focus();
      capture();
      e.preventDefault();
      commit(index, value, true);
      return;
    }

    // Inside the selection: slide it. Outside: jump the nearer endpoint here.
    if (target === fill || (value > low && value < high)) {
      drag = { kind: 'range', grabbedAt: value, fromLow: low, span: high - low };
      fill.classList.add('dragging');
      fill.focus();
      capture();
      e.preventDefault();
      return;
    }

    const index = nearestThumb(value);
    drag = { kind: 'thumb', index };
    thumbs[index]?.focus();
    capture();
    e.preventDefault();
    commit(index, value, true);
  });

  host.addEventListener('pointermove', (e) => {
    if (!drag) return;
    if (drag.kind === 'thumb') commit(drag.index, valueAt(e.clientX), true);
    else shiftRangeTo(drag.fromLow, drag.span, valueAt(e.clientX) - drag.grabbedAt, true);
  });

  const stop = (e: PointerEvent): void => {
    if (!drag) return;
    drag = null;
    fill.classList.remove('dragging');
    try {
      if (host.hasPointerCapture(e.pointerId)) host.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  host.addEventListener('pointerup', stop);
  host.addEventListener('pointercancel', stop);

  thumbs.forEach((thumb, i) => {
    const index = i as ThumbIndex;
    thumb.addEventListener('keydown', (e) => {
      const current = index === 0 ? low : high;
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = current - step;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next = current + step;
          break;
        case 'PageDown':
          next = current - step * 12;
          break;
        case 'PageUp':
          next = current + step * 12;
          break;
        case 'Home':
          next = opts.min;
          break;
        case 'End':
          next = opts.max;
          break;
        default:
          return;
      }
      e.preventDefault();
      e.stopPropagation();
      commit(index, next, true);
    });
  });

  fill.addEventListener('keydown', (e) => {
    const coarse = step * 12;
    let delta: number | null = null;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        delta = -step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        delta = step;
        break;
      case 'PageDown':
        delta = -coarse;
        break;
      case 'PageUp':
        delta = coarse;
        break;
      case 'Home':
        delta = opts.min - low;
        break;
      case 'End':
        delta = opts.max - high;
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
    shiftBy(delta);
  });

  paint();

  return {
    setValues(nextLow, nextHigh) {
      // Never fight a drag in progress.
      if (drag) return;
      low = clamp(quantize(nextLow), opts.min, opts.max);
      high = clamp(quantize(nextHigh), low + minGap, opts.max);
      paint();
    },
    get low() {
      return low;
    },
    get high() {
      return high;
    },
  };
}
